"use client"

import { useEffect, useMemo, useState } from "react"
import { ImageIcon } from "lucide-react"
import { Skeleton } from "@/components/ui/skeleton"
import { cn, formatPrice } from "@/lib/utils"
import { useHeicImage } from "@/hooks/use-heic-image"
import { isHeicUrl } from "@/lib/image-url-helper"
import { SQUARE_LG, imagePresetUrl } from "@/lib/images/presets"
import {
  formatSavedConditionLabel,
  savedConditionLook,
  savedGradeBg,
  shouldShowSavedGrade,
} from "@/lib/saved-item-row"

export function SavedItemThumb({ src, alt }: { src: string; alt: string }) {
  const isHeic = isHeicUrl(src)
  const { displaySrc, isConverting } = useHeicImage(src, { enabled: isHeic })
  const [loaded, setLoaded] = useState(false)

  // `size-[100px]` `object-cover` -> 200 device px at 2x DPR, rounded UP to
  // SQUARE_LG. The private `THUMB_SIZE 100` / `THUMB_QUALITY 50` constants this
  // replaces asked for exactly 1x DPR — soft on every retina screen — and were
  // a one-row preset table hidden in a component.
  // `isHeic` is tested on the RAW `src`, BEFORE any rewrite, so the suffix
  // check still sees `.heic` (cf. the trap in `isHeicUrl`).
  const imgSrc = useMemo(() => {
    if (isHeic) return displaySrc || src
    return imagePresetUrl(src, SQUARE_LG)
  }, [displaySrc, isHeic, src])

  useEffect(() => {
    if (!imgSrc) setLoaded(true)
    else setLoaded(false)
  }, [imgSrc])

  const showSkeleton = isConverting || (imgSrc && !loaded)

  return (
    <>
      {showSkeleton ? <Skeleton className="absolute inset-0 z-10" /> : null}
      {imgSrc ? (
        <img
          src={imgSrc}
          alt={alt}
          className={cn(
            "size-full object-cover transition-opacity duration-300",
            !loaded && "opacity-0",
          )}
          loading="lazy"
          decoding="async"
          draggable={false}
          onLoad={() => setLoaded(true)}
          onError={() => setLoaded(true)}
        />
      ) : (
        <div className="flex size-full items-center justify-center">
          <ImageIcon className="size-6 text-muted-foreground" />
        </div>
      )}
    </>
  )
}

export type SavedListingPreviewCardProps = {
  title: string
  imageUrl?: string | null
  price: number | null
  currency?: string | null
  condition?: string | null
  grade?: string | number | null
  tone?: "default" | "sheet"
  priceStrikethrough?: boolean
  className?: string
}

export function SavedListingPreviewCard({
  title,
  imageUrl,
  price,
  currency,
  condition,
  grade,
  tone = "default",
  priceStrikethrough = false,
  className,
}: SavedListingPreviewCardProps) {
  const conditionLabel = formatSavedConditionLabel(condition)
  const showGrade = shouldShowSavedGrade({ condition, grade })
  const cLook = savedConditionLook(condition)
  const priceText =
    price != null ? formatPrice(price, currency?.trim() || "USD") : null
  const sheet = tone === "sheet"

  return (
    <div
      className={cn(
        "flex w-full items-stretch gap-3 rounded-2xl px-3 py-2",
        sheet ? "bg-neutral-800/60" : "bg-background",
        className,
      )}
    >
      <div
        className={cn(
          "relative size-[100px] shrink-0 overflow-hidden rounded-2xl bg-muted",
          sheet ? "ring-[1.5px] ring-neutral-700" : "ring-[1.5px] ring-zinc-200 dark:ring-zinc-700",
        )}
      >
        {imageUrl ? (
          <SavedItemThumb src={imageUrl} alt={title} />
        ) : (
          <div className="flex size-full items-center justify-center">
            <ImageIcon className={cn("size-6", sheet ? "text-neutral-500" : "text-muted-foreground")} />
          </div>
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col justify-between py-1">
        <div className="flex min-w-0 flex-col gap-1.5">
          <h3
            className={cn(
              "line-clamp-2 text-[16px] font-semibold leading-snug",
              sheet ? "text-white" : "text-foreground",
            )}
          >
            {title}
          </h3>

          {conditionLabel || showGrade ? (
            <div className="flex items-center gap-1.5">
              {conditionLabel ? (
                <span
                  className={cn(
                    "rounded-full px-2 py-0.5 text-[11px] font-medium",
                    sheet ? "bg-neutral-700 text-neutral-300" : cLook.className,
                  )}
                >
                  {conditionLabel}
                </span>
              ) : null}
              {showGrade ? (
                <span
                  className="rounded-full px-2 py-0.5 text-[11px] font-semibold text-white"
                  style={{ backgroundColor: savedGradeBg(grade) }}
                >
                  {grade}/10
                </span>
              ) : null}
            </div>
          ) : null}
        </div>

        {priceText ? (
          <div
            className={cn(
              "flex items-center gap-2",
              sheet ? "justify-end" : "justify-between",
            )}
          >
            <span
              className={cn(
                "text-[18px] font-bold",
                sheet ? "text-white" : "text-foreground",
                priceStrikethrough && "text-neutral-500 line-through decoration-neutral-500",
              )}
            >
              {priceText}
            </span>
          </div>
        ) : null}
      </div>
    </div>
  )
}
